/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { ImageWithFallback } from '@mpb-com/feather/components/atoms/image-with-fallback';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent } from 'react';
import styled from 'styled-components';

import { ProductConditionValue } from '@toucan/types/product';

import { fallbackImagePath } from '@toucan/helpers/products';

import { ProductDetails } from '@toucan/components/views/product/components/product-details';

import { ModelOverview } from '@toucan/components/organisms/model-overview';

import { ProductTrustBlock } from '@toucan/components/molecules/product-trust-block';

import {
    Carousel,
    CarouselVariant,
} from '@toucan/client/blocks/components/carousel';
import { modelToModelOverviewProps } from '@toucan/client/features/model/hooks/use-model-to-model-overview';
import { Breadcrumb } from '@toucan/client/features/navigation/components/breadcrumb';
import { ProductState } from '@toucan/client/global/product';
import {
    ConstrainedWrapper,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { FormattedCondition } from '@toucan/client/shared/components/formatted-condition';
import { PaginationVariant } from '@toucan/client/shared/components/pagination/pagination.helpers';
import { ModelRecommendationsCarousel } from '@toucan/features/recommendations/model-recommendations-carousel';

import { ProductAddToBasket } from './components/product-add-to-basket';
import { ProductAddToQuoteLink } from './components/product-add-to-quote-link';
import { ProductDetailSidebar } from './components/product-detail-sidebar';
import { ProductOutOfStock } from './components/product-out-of-stock';
import { ProductPrice } from './components/product-price';
import { DEFAULT_TEST_ID } from './constants';
import { styles } from './product.styles';
import { ProductProps } from './types';

const StyledProduct = styled.div`
    ${styles}
`;

export const Product: FunctionComponent<ProductProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    productInfo,
    pageData,
    stockInfo,
    breadcrumbs,
    parentCategory,
    modelInfo,
    modelRecommendations,
    otherStock,
    refreshProductData,
    ...otherProps
}: ProductProps) => {
    const { t } = useTranslation();

    const isNewProduct = productInfo.condition === ProductConditionValue.NEW;
    const isOutOfStock = productInfo.state !== ProductState.ONLINE;

    const modelOverViewProps = modelToModelOverviewProps(
        t,
        modelInfo,
        isNewProduct
    );

    const imageNotice = !isNewProduct
        ? t(
              "We always photograph each unique item, so this is the exact product you'll receive."
          )
        : '';

    return (
        <StyledProduct {...otherProps} data-testid={dataTestId}>
            <ConstrainedWrapper
                className="constrained-wrapper"
                type={ConstrainedWrapperType.PADDING}
            >
                <Breadcrumb
                    links={breadcrumbs}
                    className="breadcrumb"
                    data-testid={`${dataTestId}__breadcrumb`}
                />
                <div
                    className="product-images-carousel-container"
                    data-testid={`${dataTestId}__product-carousel`}
                >
                    <Carousel
                        showExpandButton
                        variant={CarouselVariant.MINIMAL}
                        paginationVariant={PaginationVariant.THUMBNAIL}
                        maxSlideHeight="50vh"
                        note={imageNotice}
                        items={productInfo.images.map(
                            ({ alt, imageUrl }, index) => (
                                <ImageWithFallback
                                    key={index}
                                    alt={alt}
                                    src={imageUrl}
                                    fallbackSrc={fallbackImagePath}
                                />
                            )
                        )}
                        paginationItems={productInfo.images.map(
                            ({ alt, thumb }) => ({
                                image: (
                                    <ImageWithFallback
                                        alt={alt}
                                        src={thumb}
                                        fallbackSrc={fallbackImagePath}
                                    />
                                ),
                            })
                        )}
                    />
                </div>
                <div className="product-details-container">
                    <Heading
                        className="product-name"
                        data-testid={`${dataTestId}__heading`}
                        size={HeadingSize.H2}
                        as="h1"
                    >
                        <strong>{productInfo.name}</strong>
                    </Heading>
                    <FormattedCondition
                        className="product-condition"
                        data-testid={`${dataTestId}__product-condition`}
                        condition={productInfo.condition}
                        boldConditionValue={false}
                        conditionDescription={productInfo.conditionDescription}
                    />
                    <ProductPrice
                        className="product-price"
                        listPrice={parseFloat(productInfo.listPrice)}
                        originalPrice={parseFloat(
                            productInfo.productPriceOriginal
                        )}
                    />
                    <div className="product-actions">
                        {isOutOfStock ? (
                            <ProductOutOfStock
                                className="product-out-of-stock"
                                productData={productInfo}
                                data-testid={`${dataTestId}__product-out-of-stock`}
                                stockInfo={stockInfo}
                                parentCategory={parentCategory}
                            />
                        ) : (
                            <Fragment>
                                <ProductAddToBasket
                                    className="product-add-to-basket"
                                    productData={productInfo}
                                />
                                <ProductAddToQuoteLink
                                    productData={productInfo}
                                    refreshProductData={refreshProductData}
                                />
                            </Fragment>
                        )}
                    </div>
                    <ProductDetails productInfo={productInfo} />
                    {pageData && !isNewProduct && (
                        <ProductDetailSidebar
                            product={productInfo}
                            pageData={pageData}
                            className="product-detail-sidebar"
                            data-testid={`${dataTestId}__product-detail-sidebar`}
                        />
                    )}
                </div>
            </ConstrainedWrapper>
            {modelOverViewProps && (
                <ModelOverview
                    {...modelOverViewProps}
                    otherStock={otherStock}
                    className="model-overview"
                    data-testid={`${dataTestId}__model-overview`}
                />
            )}
            <ModelRecommendationsCarousel
                data-testid={`${dataTestId}__model-recommendations-carousel`}
                pageModel={modelInfo}
                pageProduct={productInfo}
                modelRecommendations={modelRecommendations}
            />
            <ProductTrustBlock data-testid={`${dataTestId}__trust-block`} />
        </StyledProduct>
    );
};
